import { inject, Injectable, NgZone } from '@angular/core';
import { FCM } from '@capacitor-community/fcm';
import { Capacitor } from '@capacitor/core';
import { ActionPerformed, PushNotifications } from '@capacitor/push-notifications';
import { AndroidSettings, IOSSettings, NativeSettings } from 'capacitor-native-settings';
import { BehaviorSubject } from 'rxjs';
import { AppDeviceService } from './app-device.service';
import { Router } from '@angular/router';
import { LoggerService } from '@work-frontend-monorepo/work-ui/services';
import { environment } from '@work-frontend-monorepo/work-environments';
import { AlertDialogService } from '@work-frontend-monorepo/work-ui/modals';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class PushNotificationsService {
  public pushNotificationToken$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private ngZone = inject(NgZone);
  private alertDialogService = inject(AlertDialogService);
  private translateService = inject(TranslateService);
  private appDeviceService = inject(AppDeviceService);
  private router = inject(Router);
  private shouldSaveToken = false;
  private pendingToken: string | null = null;

  /**
   * Initialize push notifications (permissions + listeners) without saving token yet.
   * This should be called on app start.
   */
  public async initialize(forceRegistration = false) {
    if (!Capacitor.isNativePlatform()) {
      LoggerService.LOG(this, 'push notification registration disabled on web');
      return;
    }

    let permStatus = await PushNotifications.checkPermissions();

    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions();
    }

    if (permStatus.receive !== 'granted') {
      if (forceRegistration) {
        this.enablePushNotifications();
      }
      throw new Error('User denied permissions!');
    }

    await this.registerListeners();
    await PushNotifications.register();
  }

  /**
   * Enable token saving and save pending token if available.
   * This should be called after user login.
   */
  public async enableTokenSaving() {
    this.shouldSaveToken = true;
    if (this.pendingToken) {
      await this.saveToken(this.pendingToken);
      this.pendingToken = null;
    }
  }

  /**
   * Save token via API.
   */
  private async saveToken(token: string) {
    if (!this.shouldSaveToken) {
      this.pendingToken = token;
      return;
    }
    await this.appDeviceService.initializeAppDeviceInformation(token);
  }

  /**
   * Check for push notification permissions, listen for permissions,
   * tokens, messages etc. Force for employees.
   * @deprecated Use initialize() and enableTokenSaving() instead
   */
  public async register(forceRegistration = false) {
    await this.initialize(forceRegistration);
    await this.enableTokenSaving();
  }

  /**
   * Listen for registration token, new push notifications etc.
   */
  private async registerListeners() {
    await PushNotifications.addListener('registration', (token) => {
      if (token) {
        LoggerService.LOG(this, 'Registration token: ', token.value);
        if (Capacitor.getPlatform() === 'ios') {
          FCM.getToken().then((fcmTokenResponse) => {
            if (fcmTokenResponse) {
              this.pushNotificationToken$.next(fcmTokenResponse.token);
              this.saveToken(fcmTokenResponse.token).then();
            }
          });
        } else {
          this.pushNotificationToken$.next(token.value);
          this.saveToken(token.value).then();
        }
      }
    });

    await PushNotifications.addListener('registrationError', (err) => {
      LoggerService.ERROR(this, 'Registration error: ', err.error);
    });

    await PushNotifications.addListener('pushNotificationReceived', (notification) => {
      LoggerService.LOG(this, 'Push notification received: ', notification);
    });

    this.listenForPushNotificationActions();
  }

  private enablePushNotifications() {
    PushNotifications.requestPermissions().then((permissionStatus) => {
      this.ngZone.run(async () => {
        if (permissionStatus.receive === 'denied') {
          this.alertDialogService.showAlert({
            title: 'BERECHTIGUNGEN',
            message: this.translateService.instant('PUSH_BERECHTIGUNG_HINWEIS', { app: environment.tenantCompanyCommonName }),
            alertLifeTimeInMs: 60 * 1000,
            actionHandler: async () => {
              await NativeSettings.open({
                optionAndroid: AndroidSettings.AppNotification,
                optionIOS: IOSSettings.App,
              });
            },
          });
        }
      });
    });
  }

  /**
   * Liste for actions and redirect to correct route.
   * @private
   */
  private listenForPushNotificationActions() {
    PushNotifications.addListener('pushNotificationActionPerformed', (res: ActionPerformed) => {
      this.ngZone.run(() => {
        if (res.notification.data && res.notification.data.relatedEntityId && res.notification.data.relatedEntityId !== 'undefined') {
          try {
            switch (res.notification.data.relatedEntityType) {
              case 'event':
                this.router.navigateByUrl(`/veranstaltungen/details/${res.notification.data.relatedEntityId}`).then();
                break;
              case 'news':
                this.router.navigateByUrl(`/mitteilungen/details/${res.notification.data.relatedEntityId}`).then();
                break;
              case 'absence':
                this.router.navigateByUrl(`/absenzen/anfrage/${res.notification.data.relatedEntityId}`).then();
                break;
              case 'equipment':
                this.router.navigateByUrl(`/betriebsmittel/details/${res.notification.data.relatedEntityId}`).then();
                break;
            }
          } catch (readNotificationDataException) {
            LoggerService.ERROR(this, 'readNotificationDataException', readNotificationDataException);
          }
        } else if (res.notification.data && res.notification.data.routeApp) {
          this.router.navigateByUrl(res.notification.data.routeApp).then();
        }
      });
    });
  }
}
